<template>
  <!-- ===== 服务器租赁板块 ===== -->
  <section class="rental-section">
    <div class="rental-bg">
      <div class="rental-orb rental-orb-1"></div>
      <div class="rental-orb rental-orb-2"></div>
    </div>
    <div class="section-header">
      <!-- <span class="section-badge">🖥️ 算力租赁</span> -->
      <h2 class="section-title">
        {{ $t("serverList.title1")
        }}<span class="highlight">{{ $t("serverList.title2") }}</span>
      </h2>
      <p class="section-desc">{{ $t("serverList.desc") }}</p>
    </div>

    <div class="rental-grid">
      <!-- 合并两个列表，统一渲染 -->
      <div
        class="rental-card"
        v-for="server in serverList"
        :key="server.id"
        :class="{ 'rental-highlight': server.highlight }"
      >
        <!-- 推荐标签 -->
        <div class="rental-card-badges">
          <span
            v-if="server.highlight > 0"
            :class="['badge', getHighlightClass(server.highlight)]"
            >{{ getHighlightText(server.highlight) }}</span
          >
        </div>

        <!-- 服务器名称 -->
        <div class="rental-header">
          <ServerIconIsometric size="sm" />

          <h3 class="rental-name">{{ $t(server.code + ".s_model") }}</h3>
          <!-- <span class="rental-spec">{{ ServerSpec?.[server.spec] }}</span> -->
        </div>

        <!-- 核心数据 - 固定宽度 -->
        <div class="rental-stats">
          <div class="rental-stat" style="min-width: 60px" v-if="getServerStatus(server) === STATUS.active">
            <span class="rental-stat-label">{{
              $t("serverList.dailyIncome")
            }}</span>
            <span class="rental-stat-value rental-price"
              >${{ server.today_income }}</span
            >
          </div>
          <div class="rental-divider" v-if="getServerStatus(server) == STATUS.active"></div>
          <div class="rental-stat" style="min-width: 60px">
            <span class="rental-stat-label">{{ $t("serverList.value") }}</span>
            <span class="rental-stat-value">${{ server.value }}</span>
          </div>
          <div class="rental-divider" v-if="server.yield"></div>
          <div class="rental-stat" style="min-width: 50px" v-if="server.yield">
            <span class="rental-stat-label">{{ $t("serverList.yield") }}</span>
            <span class="rental-stat-value rental-yield">{{
              server.yield
            }}</span>
          </div>
        </div>

        <!-- 价值进度条 -->
        <div class="rental-value-progress">
          <!-- <div class="value-header">
            <div class="value-info">
              <span class="value-label">价值进度</span>
            </div> 
            <span class="value-progress-text">
              {{ calcValueProgress(server) }}%
            </span>
          </div> -->

          <div class="progress-track">
            <div
              class="progress-bar"
              v-if="getServerStatus(server) === STATUS.active"
              :style="`width: ${calcValueProgress(server)}%`"
            >
              <span class="progress-glow"></span>
            </div>
            <span class="progress-box" v-if="getServerStatus(server) === STATUS.active"> {{ calcValueProgress(server) }}% </span>
          </div>

          <!-- <div class="value-footer">
            <span class="value-remaining">
              剩余
              <strong>${{ calcValueAmount(server) }}</strong>
            </span>
          </div> -->
        </div>

        <!-- 额外信息 - 固定宽度 -->
        <div class="rental-details">
          <div class="rental-detail-item">
            <span class="rental-detail-label">GPU</span>
            <span class="rental-detail-value">{{
              $t(server.code + ".s_core")
            }}</span>
          </div>
          <div class="rental-detail-item">
            <span class="rental-detail-label">{{ $t("serverList.vram") }}</span>
            <span class="rental-detail-value">{{
              $t(server.code + ".s_vram")
            }}</span>
          </div>
          <div class="rental-detail-item">
            <span class="rental-detail-label">{{
              $t("serverList.storage")
            }}</span>
            <span class="rental-detail-value">{{
              $t(server.code + ".s_ssd")
            }}</span>
          </div>
          <div class="rental-detail-item">
            <span class="rental-detail-label">{{
              $t("serverList.status")
            }}</span>
            <span
              class="rental-detail-value"
              :class="
                getServerStatus(server) === STATUS.active &&
                calcValueProgress(server) < 100
                  ? 'available-yes'
                  : 'available-no'
              "
              style="min-width: 56px"
            >
              {{
                calcValueProgress(server) >= 100
                  ? $t("serverList.full")
                  : getServerStatus(server) === STATUS.ended
                  ? $t("serverList.btnEnded")
                  : getServerStatus(server) === STATUS.pending
                  ? $t("serverList.btnWait")
                  : getServerStatus(server) === STATUS.active
                  ? $t("serverList.available")
                  : $t("serverList.full")
              }}
            </span>
          </div>
        </div>

        <!-- 倒计时 -->
        <div v-if="getServerStatus(server) === STATUS.pending" class="countdown-box">
          <span class="countdown-label">{{ $t("serverList.countdown") }}</span>
          <span class="countdown-time">{{ formatCountdown(server) }}</span>
        </div>

        <!-- 操作按钮 -->
        <button
          v-if="getServerStatus(server) !== STATUS.pending"
          class="btn-rental"
          :class="{ 'btn-full': calcValueProgress(server) >= 100 }"
          :disabled="
            getServerStatus(server) !== STATUS.active ||
            calcValueProgress(server) >= 100
          "
          @click="handleRental(server)"
        >
          {{
            calcValueProgress(server) >= 100
              ? $t("serverList.btnFull")
              : getServerStatus(server) === STATUS.active
              ? $t("serverList.btnRent")
              : getServerStatus(server) === STATUS.pending
              ? $t("serverList.btnWait")
              : $t("serverList.btnEnded")
          }}
        </button>
      </div>
    </div>

    <!-- ===== 更多产品按钮 ===== -->
    <div class="more-products">
      <button class="btn-more" @click="handleMoreProducts">
        <span class="btn-more-text">{{ $t("serverList.more") }}</span>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path
            d="M5 12H19M19 12L12 5M19 12L12 19"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
  </section>
</template>

<script>
const STATUS = {
  active: "active",
  pending: "pending",
  ended: "ended",
}
const ServerSpec = {
  1: "serverList.spec1",
  2: "serverList.spec2",
  3: "serverList.spec3",
  4: "serverList.spec4",
};

const ServerHighlightMap = {
  1: {
    textKey: "serverList.tagHot",
    class: "badge-hot",
  },
  2: {
    textKey: "serverList.tagRecommend",
    class: "badge-recommend",
  },
  3: {
    textKey: "serverList.tagNew",
    class: "badge-available",
  },
};
import { loadProductsLanguage } from "@/locales";
import ServerIconIsometric from "@/components/server/ServerIconIsometric.vue";
export default {
  components: {
    ServerIconIsometric,
  },
  data() {
    return {
      STATUS,
      ServerSpec,
      serverList: [],
      now: Date.now(),
      countdownTimer: null,
    };
  },
  created() {
    this.getServerTop();
    // 启动倒计时定时器，每秒更新
    this.countdownTimer = setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  },
  watch: {
    "$i18n.locale": function (newLang, oldLang) {
      console.log(`语言从 ${oldLang} 切换到 ${newLang}`);
      // 重新加载产品翻译（如果切换到的新语言还没有该产品翻译）
      this.loadProductTranslations();
    },
  },
  methods: {
    async loadProductTranslations() {
      //批量加载产品翻译
      const codes = this.serverList.map((p) => p.code);
      await loadProductsLanguage(codes, {
        path: "server/",
        module: "server",
      });
    },
    getServerTop() {
      this.$api.server
        .getServerTop()
        .then((res) => {
          if (res.code === 0) {
            this.serverList = res.data || [];
            this.loadProductTranslations();
          }
        })
        .catch((err) => {
          console.error("获取服务器列表失败:", err);
        });
    },

    // ===== 判断服务器状态 =====
    // pending: 未开始（倒计时中）
    // active: 进行中（可租赁）
    // ended: 已结束（不可租赁）
    getServerStatus(server) {
      const now = this.now;
      const beginTime = server.begin_time
        ? new Date(server.begin_time).getTime()
        : null;
      const endTime = server.end_time
        ? new Date(server.end_time).getTime()
        : null;

      // 没有时间信息，默认按 status 判断
      if (!beginTime && !endTime) {
        return server.status == 2 ? STATUS.active : STATUS.ended;
      }

      // 未到开始时间
      if (beginTime && now < beginTime) {
        return STATUS.pending;
      }

      // 已过结束时间
      if (endTime && now > endTime) {
        return STATUS.ended;
      }

      return STATUS.active;
    },

    // ===== 格式化倒计时 =====
    formatCountdown(server) {
      const beginTime = server.begin_time
        ? new Date(server.begin_time).getTime()
        : null;
      if (!beginTime) return "";

      const diff = beginTime - this.now;
      if (diff <= 0) return "00:00:00";

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      const pad = (n) => String(n).padStart(2, "0");

      if (days > 0) {
        return `${days}${this.$t("serverList.days")} ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
      }
      return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    },
    getHighlightText(highlight) {
      const key = ServerHighlightMap[highlight]?.textKey;
      return key ? this.$t(key) : "";
    },
    getHighlightClass(highlight) {
      return ServerHighlightMap[highlight]?.class || "";
    },
    calcValueProgress(item) {
      const rate = (1 - (item.surplus - item.sale)) * 100;
      if (!rate) return 0;
      return Math.min(Math.round(rate), 100);
    },
    calcValueAmount(item) {
      const amount = (item.surplus - item.sale) * item.value;
      if (!amount) return 0;
      return Math.max(Math.round(amount), 0);
    },
    handleRental(server) {
      this.$router.push({
        name: "RentalDetail",
        query: {
          code: server.code,
        },
      });
    },
    handleMoreProducts() {
      // 跳转到产品列表页
      this.$router.push("/rental");
      // 或者触发事件
      // this.$emit('more-products');
    },
  },
};
</script>

<style scoped>
/* ============================================================
   服务器租赁板块
   ============================================================ */
.rental-section {
  padding: 80px 20px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #0a0e1a, #141b2d);
}

.rental-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.rental-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.08;
}

.rental-orb-1 {
  width: 400px;
  height: 400px;
  background: #f7971e;
  top: -100px;
  right: -100px;
}

.rental-orb-2 {
  width: 300px;
  height: 300px;
  background: #7c3aed;
  bottom: -100px;
  left: -50px;
}

.section-header {
  text-align: center;
  margin-bottom: 48px;
  position: relative;
  z-index: 1;
}

.section-badge {
  display: inline-block;
  padding: 4px 16px;
  border-radius: 20px;
  background: rgba(0, 180, 255, 0.1);
  color: #00b4ff;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 12px;
}

.section-title {
  font-size: 36px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 8px 0;
}

.section-title .highlight {
  background: linear-gradient(135deg, #f7971e, #ffd200);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.section-desc {
  color: rgba(255, 255, 255, 0.4);
  font-size: 16px;
  margin: 0;
}

/* ===== 卡片网格 ===== */
.rental-grid {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  position: relative;
  z-index: 1;
}

/* ===== 卡片 ===== */
.rental-card {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 20px 18px 18px;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 0;
}

.rental-card:hover {
  transform: translateY(-4px);
  border-color: rgba(0, 180, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
}

.rental-highlight {
  border-color: rgba(255, 215, 0, 0.2);
  background: linear-gradient(
    135deg,
    rgba(255, 215, 0, 0.04),
    rgba(0, 180, 255, 0.04)
  );
}

.rental-highlight:hover {
  border-color: rgba(255, 215, 0, 0.3);
}

/* ===== 标签 ===== */
.rental-card-badges {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.badge {
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.badge-hot {
  background: rgba(255, 152, 0, 0.15);
  color: #ff9800;
}

.badge-available {
  background: rgba(0, 201, 81, 0.12);
  color: #00c951;
}

.badge-sold {
  background: rgba(255, 107, 107, 0.12);
  color: #ff6b6b;
}

.badge-recommend {
  background: rgb(255 235 59 / 11%);
  color: #ffeb3b;
}

/* ===== 头部 ===== */
.rental-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  min-height: 44px;
}

.rental-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(0, 180, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00b4ff;
  flex-shrink: 0;
}

.rental-name {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  min-width: 80px;
  /* max-width: 120px; */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
}

.rental-spec {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.04);
  padding: 2px 10px;
  border-radius: 12px;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
}

/* ===== 核心数据 ===== */
.rental-stats {
  display: flex;
  align-items: stretch;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 10px;
  padding: 10px 6px;
  margin-bottom: 14px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.rental-stat {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  padding: 0 2px;
  min-width: 50px;
  justify-content: center;
}

.rental-stat-label {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.25);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.rental-stat-value {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  font-family: "SF Mono", "Courier New", monospace;
  white-space: nowrap;
}

.rental-price {
  color: #ffd866;
  font-size: 17px;
}

.rental-yield {
  color: #00c951;
}

.rental-divider {
  width: 1px;
  background: rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

/* ===== 价值进度条 ===== */
.rental-value-progress {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.value-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.value-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.value-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.value-progress-text {
  font-size: 14px;
  font-weight: 700;
  background: linear-gradient(135deg, #00b4ff, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.progress-track {
  position: relative;
  width: 100%;
  height: 15px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  overflow: visible;
}

.progress-bar {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #00b4ff, #7c3aed);
  position: relative;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.progress-glow {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  background: radial-gradient(circle, rgba(0, 180, 255, 0.4), transparent 70%);
  border-radius: 50%;
  animation: glow-pulse 2s ease-in-out infinite;
}

@keyframes glow-pulse {
  0%,
  100% {
    opacity: 0.6;
    transform: translateY(-50%) scale(1);
  }
  50% {
    opacity: 1;
    transform: translateY(-50%) scale(1.3);
  }
}

.value-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.03);
}

.value-remaining {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
}

.value-remaining strong {
  color: #ffd866;
  font-weight: 700;
}

/* ===== 详情信息 ===== */
.rental-details {
  display: grid;
  gap: 2px 12px;
  margin-bottom: 14px;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.rental-detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
  gap: 8px;
  overflow: hidden;
}

.rental-detail-label {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.25);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
  min-width: 28px;
}

.rental-detail-value {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 40px;
  flex-shrink: 1;
}

.available-yes {
  color: #00c951;
}

.available-no {
  color: #ff6b6b;
}

/* ===== 倒计时 ===== */
.countdown-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 10px;
  border-radius: 10px;
  background: rgba(255, 152, 0, 0.08);
  border: 1px solid rgba(255, 152, 0, 0.15);
}

.countdown-label {
  font-size: 12px;
  color: rgba(255, 152, 0, 0.7);
  font-weight: 500;
  white-space: nowrap;
}

.countdown-time {
  font-size: 15px;
  font-weight: 800;
  color: #ff9800;
  font-family: "SF Mono", "Courier New", monospace;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

/* ===== 按钮 ===== */
.btn-rental {
  width: 100%;
  padding: 10px 0;
  border-radius: 10px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  background: linear-gradient(135deg, #00b4ff, #0066ff);
  color: #fff;
  white-space: nowrap;
}

.btn-rental:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(0, 100, 255, 0.3);
}

.btn-rental:disabled {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.2);
  cursor: not-allowed;
}

/* ===== 已满额按钮（红色系） ===== */
.btn-rental.btn-full {
  background: rgba(255, 107, 107, 0.12);
  color: #ff6b6b;
  border: 1px solid rgba(255, 107, 107, 0.2);
  cursor: not-allowed;
}

/* ===== 更多产品按钮 ===== */
.more-products {
  display: flex;
  justify-content: center;
  margin-top: 48px;
  position: relative;
  z-index: 1;
}

.btn-more {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 40px;
  border-radius: 50px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.btn-more:hover {
  border-color: rgba(0, 180, 255, 0.3);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 180, 255, 0.05);
}

.btn-more-text {
  white-space: nowrap;
}

.btn-more svg {
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.btn-more:hover svg {
  transform: translateX(4px);
}

/* ============================================================
   响应式
   ============================================================ */
@media (max-width: 1024px) {
  .rental-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .rental-section {
    padding: 40px 16px;
  }

  .section-title {
    font-size: 28px;
  }

  .rental-grid {
    grid-template-columns: 1fr;
    max-width: 400px;
  }

  .rental-stats {
    flex-wrap: wrap;
    gap: 4px 0;
  }

  .rental-divider {
    display: none;
  }

  .rental-stat {
    flex: 1 1 45%;
    padding: 2px 0;
    min-width: 60px;
  }

  .rental-details {
    grid-template-columns: 1fr;
    gap: 2px 0;
  }

  .rental-detail-item {
    justify-content: space-between;
  }

  .rental-detail-value {
    white-space: nowrap;
    max-width: 120px;
  }

  .rental-card-badges {
    margin-bottom: 8px;
  }

  .badge {
    font-size: 12px;
    padding: 2px 8px;
  }

  .rental-name {
    max-width: 100px;
  }

  .btn-more {
    padding: 12px 24px;
    font-size: 14px;
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 380px) {
  .rental-name {
    max-width: 70px;
    font-size: 14px;
  }

  .rental-stat-value {
    font-size: 13px;
  }

  .rental-price {
    font-size: 15px;
  }
}

.progress-box {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}
</style>
